const DAY = 86400000;
const parisClock = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/Paris',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  hourCycle: 'h23',
});

export function deliveryEstimate(now, minDays, maxDays, cutoffHour = 16) {
  if (!(now instanceof Date) || !Number.isFinite(now.getTime())) return null;
  if (!Number.isInteger(minDays) || !Number.isInteger(maxDays) || minDays < 0 || maxDays < minDays) return null;
  if (!Number.isInteger(cutoffHour) || cutoffHour < 0 || cutoffHour > 23) return null;
  const parts = Object.fromEntries(parisClock.formatToParts(now).map(({ type, value }) => [type, value]));
  const tomorrow = Number(parts.hour) >= cutoffHour;
  const orderDay = Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day)) + (tomorrow ? DAY : 0);
  return {
    tomorrow,
    earliest: new Date(orderDay + minDays * DAY),
    latest: new Date(orderDay + maxDays * DAY),
  };
}

export function deliveryWindow(estimate, locale = 'fr') {
  const format = new Intl.DateTimeFormat(locale, {
    day: '2-digit', month: '2-digit', timeZone: 'UTC',
  });
  return {
    start: format.format(new Date(estimate.latest.getTime() - DAY)),
    end: format.format(new Date(estimate.latest.getTime() + DAY)),
  };
}

if (typeof customElements !== 'undefined' && !customElements.get('mk-delivery-estimate')) {
  customElements.define('mk-delivery-estimate', class extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('.shopify-section, dialog') || document;
      this.available = this.dataset.available === 'true';
      this.variants = JSON.parse(this.querySelector('[data-delivery-variants]')?.textContent || '{}');
      this.onChange = () => {
        const id = this.root.querySelector('product-form-component input[name="id"]')?.value;
        if (Object.hasOwn(this.variants, id)) this.available = this.variants[id];
        this.update();
      };
      this.onSelect = (event) => {
        if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
        if (!event.promise) return this.onChange();
        event.promise.then(({ detail }) => {
          if (detail?.productId && String(detail.productId).split('/').pop() !== this.dataset.productId) return;
          const resource = detail?.resource;
          if (resource?.id) {
            const id = String(resource.id).split('/').pop();
            this.available = resource.available ?? this.variants[id] ?? false;
          } else {
            this.available = false;
          }
          this.update();
        }).catch(() => {});
      };
      this.onVisibility = () => {
        if (!document.hidden) this.update();
      };
      this.root.addEventListener('change', this.onChange);
      this.root.addEventListener('shopify:product:select', this.onSelect);
      document.addEventListener('visibilitychange', this.onVisibility);
      this.update();
      this.timer = setInterval(() => this.update(), 1000);
    }

    disconnectedCallback() {
      this.root?.removeEventListener('change', this.onChange);
      this.root?.removeEventListener('shopify:product:select', this.onSelect);
      document.removeEventListener('visibilitychange', this.onVisibility);
      clearInterval(this.timer);
    }

    update() {
      const promise = this.querySelector('[data-delivery-promise]');
      if (!promise) return;
      const estimate = deliveryEstimate(new Date(), Number(this.dataset.minDays), Number(this.dataset.maxDays), Number(this.dataset.cutoffHour));
      promise.hidden = !this.available || !estimate;
      if (promise.hidden) return;
      const { start, end } = deliveryWindow(estimate, this.dataset.locale || 'fr');
      const text = this.dataset.estimateTemplate.replaceAll('__start__', start).replaceAll('__end__', end);
      if (promise.textContent !== text) promise.textContent = text;
    }
  });
}
